import { useState, useCallback, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { CardDisplay } from '../cards/CardDisplay'
import { ActionBar } from './ActionBar'
import { ShareModal } from '../ui/ShareModal'
import { HistoryView } from '../ui/HistoryView'
import { LeaderboardView } from '../ui/LeaderboardView'
import { GloryView } from '../ui/GloryView'
import { Confetti } from '../effects/Confetti'
import { useGameSocket } from '../../hooks/useGameSocket'
import { useLocale } from '../../App'

interface PokerTableSocketProps {
  authToken: string | null
  userName?: string
  onGameEnd?: () => void
}

interface ClientGameState {
  gameId?: string
  userHand: any[]
  aiHand: any[]
  communityCards: any[]
  userChips: number
  aiChips: number
  pot: number
  currentRound: string
  currentActor: string
  currentBet: number
  userBet: number
  aiBet: number
  status: string
  winner?: string
  winningHand?: any
  lastAIAction?: { action: string; amount: number } | null
  initialChips?: number
}

const ROUND_LABELS: Record<string, string> = {
  preflop: 'app.round_preflop',
  flop: 'app.round_flop',
  turn: 'app.round_turn',
  river: 'app.round_river',
  showdown: 'app.round_showdown',
}

export function PokerTableSocket({ authToken, userName, onGameEnd }: PokerTableSocketProps) {
  const { t, locale, toggleLocale } = useLocale()
  const { connected, gameState: wsGameState, sendAction, joinGame, error, resetState } = useGameSocket(authToken)
  const [gameId, setGameId] = useState<string | null>(() => localStorage.getItem('poker_game_id'))
  const [loading, setLoading] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [shareReason, setShareReason] = useState('')
  const [startError, setStartError] = useState<string | null>(null)
  const [initialGameState, setInitialGameState] = useState<ClientGameState | null>(null)
  const [userBalance, setUserBalance] = useState<number | null>(null)
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null)
  const [revealedAiHand, setRevealedAiHand] = useState<any[] | null>(null)
  const [view, setView] = useState<'welcome' | 'playing' | 'history' | 'leaderboard' | 'glory'>('welcome')
  const [isStarting, setIsStarting] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [aiName, setAiName] = useState('AI')
  const AI_NAMES = ['Claude', 'GPT', 'Gemini', 'Grok', 'DeepSeek', 'Qwen', 'Ernie', 'Llama', 'GLM', 'Kimi', 'Mistral', 'Perplexity', 'Cohere', 'Command', 'Reka', 'Aleph Alpha', 'Jurassic', 'Claude Sonnet', 'Yi', 'MiniMax']
  // Persisted result so the game table stays visible after game ends
  const [gameResult, setGameResult] = useState<{ winner: string; winningHand?: any } | null>(null)
  const [sharing, setSharing] = useState(false)
  const [prefetchedGame, setPrefetchedGame] = useState<{ gameId: string; gameState: any } | null>(null)

  // ---- Start a new game ----
  const startGame = useCallback(async () => {
    if (!authToken) return
    stopOldGame.current = true
    gameEndHandledRef.current = false
    setIsStarting(true)
    setLoading(true)
    setStartError(null)
    setInitialGameState(null)
    setUserBalance(null)
    setUserPhotoUrl(null)
    setAiName(AI_NAMES[Math.floor(Math.random() * AI_NAMES.length)])
    localStorage.removeItem('poker_game_id')
    setGameId(null)
    resetState()

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 15000)
    try {
      const res = await fetch('/api/game/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ buyIn: 200, difficulty: 'normal' }),
        signal: controller.signal,
      })
      clearTimeout(timer)
      const data = await res.json()
       if (!res.ok) {
         if (res.status === 400 && data.message?.includes('Insufficient')) {
           setShareReason('revive')
           setShowShare(true)
         } else {
           setStartError(data.message || `Error ${res.status}`)
         }
         setLoading(false)
         setIsStarting(false)
         stopOldGame.current = false
         return
       }
       setGameResult(null)
       stopOldGame.current = false
       setGameId(data.gameId)
       localStorage.setItem('poker_game_id', data.gameId)
       setInitialGameState(data.gameState)
     } catch (err: any) {
       clearTimeout(timer)
       setStartError('连接失败，请重试')
       try { window.Telegram?.WebApp?.showPopup?.({ title: '提示', message: '连接失败，请重试', buttons: [{ id: 'ok', type: 'close', text: '确定' }] }) } catch {}
       stopOldGame.current = false
     }
     setLoading(false)
     setIsStarting(false)
     stopOldGame.current = false
  }, [authToken])

  const handlePlayAgain = useCallback(() => {
    if (prefetchedGame) {
      stopOldGame.current = true
      gameEndHandledRef.current = false
      setGameResult(null)
      setGameId(prefetchedGame.gameId)
      localStorage.setItem('poker_game_id', prefetchedGame.gameId)
      setInitialGameState(prefetchedGame.gameState)
      setPrefetchedGame(null)
      stopOldGame.current = false
    } else {
      startGame()
    }
  }, [prefetchedGame])

  // ---- Join game room when we have a gameId + connection ----
  useEffect(() => {
    if (gameId && connected) {
      joinGame(gameId)
    }
  }, [gameId, connected, joinGame])

  // ---- Save revealed AI cards when status=revealing ----
  useEffect(() => {
    if (wsGameState?.status === 'revealing' && wsGameState.aiHand) {
      setRevealedAiHand(wsGameState.aiHand)
    }
  }, [wsGameState])

  // ---- Sync: clear REST state once WebSocket takes over ----
  useEffect(() => {
    if (wsGameState && wsGameState.gameId === gameId) {
      setInitialGameState(null)
    }
  }, [wsGameState, gameId])

  // ---- Detect game completion ----
  useEffect(() => {
    if (stopOldGame.current) return
    if (wsGameState?.status === 'completed' && wsGameState.winner) {
      if (wsGameState.gameId && wsGameState.gameId !== gameId) return
      if (gameEndHandledRef.current) return
      gameEndHandledRef.current = true
      setGameResult({ winner: wsGameState.winner, winningHand: wsGameState.winningHand })
      onGameEnd?.()
      if (wsGameState.winner === 'user') {
        setShowConfetti(true)
      }

      fetch('/api/user/profile', {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((r) => r.json())
        .then((profile) => {
          if (profile.chips < 20) {
            setShareReason('revive')
            setShowShare(true)
          }
        })
        .catch(() => {})

      fetch('/api/game/prefetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ difficulty: 'normal' }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.gameId) setPrefetchedGame({ gameId: data.gameId, gameState: data.gameState })
        })
        .catch(() => setPrefetchedGame(null))
    }
  }, [wsGameState?.status, wsGameState?.winner, onGameEnd, authToken, gameId])

  const playerAction = useCallback(
    (action: string, amount?: number) => {
      if (!gameId) return
      sendAction(gameId, action, amount)
    },
    [gameId, sendAction],
  )

  // ---- Derived game state (REST fallback → WebSocket → default) ----
  const gameState: ClientGameState = initialGameState || wsGameState || {
    userHand: [],
    aiHand: [],
    communityCards: [],
    userChips: 0,
    aiChips: 0,
    pot: 0,
    currentRound: 'preflop',
    currentActor: 'user',
    currentBet: 0,
    userBet: 0,
    aiBet: 0,
    status: 'completed',
    lastAIAction: null,
  }

  const isRevealing = gameState.status === 'revealing'
  const isGameActive = gameState.status === 'active' || gameState.status === 'revealing'

  const handLabel = (() => {
    const wh = gameResult?.winningHand
    if (!wh?.handType && !wh?.description) return null
    const key = 'app.hand_' + wh.handType
    const fromI18n = t(key)
    return fromI18n !== key ? fromI18n : wh.description
  })()

  // ---- Stop old game events when starting new game ----
  const stopOldGame = useRef(false)
  // Prevent duplicate game-end effect execution (defense against stale closures)
  const gameEndHandledRef = useRef(false)

  // ---- AI action label animation ----
  const aiLabelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (gameState.lastAIAction?.action && aiLabelRef.current) {
      gsap.fromTo(aiLabelRef.current,
        { opacity: 0, y: -8, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: 'back.out(1.5)' }
      )
    }
  }, [gameState.lastAIAction])

  // ---- Card dealing animation ----
  useEffect(() => {
    if (!initialGameState) return
    const timer = setTimeout(() => {
      const tl = gsap.timeline({ defaults: { duration: 0.3, ease: 'back.out(1.7)' } })
      tl.fromTo('.user-cards > *', { opacity: 0, x: 60, scale: 0.5 }, { opacity: 1, x: 0, scale: 1, stagger: 0.08 })
      tl.fromTo('.ai-cards > *', { opacity: 0, x: -60, scale: 0.5 }, { opacity: 1, x: 0, scale: 1, stagger: 0.08 }, '-=0.16')
    }, 100)
    return () => clearTimeout(timer)
  }, [initialGameState])

  // ---- Chip flying animation ----
  const potRef = useRef<HTMLDivElement>(null)
  const prevPot = useRef(0)
  const aiSectionRef = useRef<HTMLDivElement>(null)
  const userSectionRef = useRef<HTMLDivElement>(null)
  const chipLayerRef = useRef<HTMLDivElement>(null)

  const flyChip = useCallback((from: 'ai' | 'user', count: number) => {
    const layer = chipLayerRef.current
    const src = from === 'ai' ? aiSectionRef.current : userSectionRef.current
    const dst = potRef.current
    if (!layer || !src || !dst || count <= 0) return
    const srcRect = src.getBoundingClientRect()
    const dstRect = dst.getBoundingClientRect()
    const sx = srcRect.left + srcRect.width / 2
    const sy = srcRect.top + srcRect.height / 2
    const dx = dstRect.left + dstRect.width / 2
    const dy = dstRect.top + dstRect.height / 2
    for (let i = 0; i < count; i++) {
      const chip = document.createElement('div')
      chip.textContent = '💰'
      chip.className = 'absolute text-lg leading-none pointer-events-none'
      chip.style.zIndex = '1000'
      chip.style.left = '0px'
      chip.style.top = '0px'
      const rx = (Math.random() - 0.5) * 16
      const ry = (Math.random() - 0.5) * 16
      const ra = (Math.random() - 0.5) * 90
      layer.appendChild(chip)
      gsap.set(chip, { x: sx, y: sy, opacity: 1 })
      gsap.to(chip, {
        x: dx + rx,
        y: dy + ry,
        rotation: ra,
        scale: 1.3,
        opacity: 0.9,
        duration: 0.45 + Math.random() * 0.15,
        ease: 'power2.in',
        onComplete: () => chip.remove(),
      })
    }
  }, [])
  useEffect(() => {
    if (gameState.pot > prevPot.current && potRef.current) {
      gsap.fromTo(potRef.current,
        { scale: 1 },
        { scale: 1.15, duration: 0.15, yoyo: true, repeat: 1, ease: 'power2.out' }
      )
    }
    prevPot.current = gameState.pot
  }, [gameState.pot])

  // ---- Bet amount flash animation ----
  const aiBetRef = useRef<HTMLDivElement>(null)
  const userBetRef = useRef<HTMLDivElement>(null)
  const prevAiBet = useRef(0)
  const prevUserBet = useRef(0)
  useEffect(() => {
    const delta = gameState.aiBet - prevAiBet.current
    if (delta > 0 && aiBetRef.current) {
      gsap.fromTo(aiBetRef.current,
        { borderColor: 'rgba(251,191,36,0.8)', boxShadow: '0 0 12px rgba(251,191,36,0.4)' },
        { borderColor: 'rgba(100,116,139,0.5)', boxShadow: '0 0 0px rgba(251,191,36,0)', duration: 0.8, ease: 'power2.out' }
      )
      flyChip('ai', Math.min(Math.ceil(delta / 50), 6) || 1)
    }
    prevAiBet.current = gameState.aiBet
  }, [gameState.aiBet])
  useEffect(() => {
    const delta = gameState.userBet - prevUserBet.current
    if (delta > 0 && userBetRef.current) {
      gsap.fromTo(userBetRef.current,
        { borderColor: 'rgba(96,165,250,0.8)', boxShadow: '0 0 12px rgba(96,165,250,0.4)' },
        { borderColor: 'rgba(100,116,139,0.5)', boxShadow: '0 0 0px rgba(96,165,250,0)', duration: 0.8, ease: 'power2.out' }
      )
      flyChip('user', Math.min(Math.ceil(delta / 50), 6) || 1)
    }
    prevUserBet.current = gameState.userBet
  }, [gameState.userBet])

  // ---- Community card flip animation ----
  const prevCommCards = useRef(0)
  useEffect(() => {
    const cur = gameState.communityCards.length
    const added = cur - prevCommCards.current
    if (added > 0) {
      const cards = document.querySelectorAll('.community-cards > .poker-card')
      const newCards = Array.from(cards).slice(-added) as HTMLElement[]
      gsap.fromTo(newCards,
        { rotateY: 90, opacity: 0 },
        { rotateY: 0, opacity: 1, duration: 0.35, stagger: 0.08, ease: 'power2.out' }
      )
    }
    prevCommCards.current = cur
  }, [gameState.communityCards.length])

  // ---- Share game result ----
  const handleShareResult = useCallback(async () => {
    if (!authToken || !gameResult || !gameState) return
    setSharing(true)
    try {
      const url = 'https://poker.usdt.bz/s/play'
      const text = t('app.share_result', { username: userName || '玩家', aiName })
      const tg = window.Telegram?.WebApp
      if (tg?.openTelegramLink) {
        tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`)
      } else if (tg?.shareUrl) {
        tg.shareUrl(url)
      } else {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank')
      }
    } finally {
      setSharing(false)
    }
  }, [authToken, gameResult, gameState, userName, aiName])

  // ---- Fetch user balance for welcome screen ----
  useEffect(() => {
    if (!isGameActive && !gameResult && authToken && userBalance === null) {
      fetch('/api/user/profile', {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((r) => r.json())
        .then((p) => { setUserBalance(p.chips); setUserPhotoUrl(p.photoUrl) })
        .catch(() => {})
    }
  }, [isGameActive, gameResult, authToken, userBalance])

  // ===== Sub-views =====
  if (view === 'history') return <HistoryView authToken={authToken} onBack={() => setView('welcome')} />
  if (view === 'leaderboard') return <LeaderboardView authToken={authToken} onBack={() => setView('welcome')} />
  if (view === 'glory') return <GloryView authToken={authToken} onBack={() => setView('welcome')} />

  // ===== Welcome Screen =====
  // Show when: no active game AND no result to display on the game table
  if (!isGameActive && !gameResult) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto flex flex-col items-center gap-6 py-10 px-4">
          <div className="poker-table flex items-center justify-center overflow-hidden shrink-0" style={{ height: '160px' }}>
            <img src="/images/homebak.webp" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="text-center shrink-0">
            <h1 className="text-[28px] font-extrabold tracking-tight text-white">♠️ Texas Hold'em</h1>
          </div>
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className="text-[15px] text-slate-300">
              {t('app.balance')}：<span className="font-bold text-yellow-400">{userBalance?.toLocaleString() ?? '...'}</span> {t('app.chips')}
            </div>
            <div className="text-[13px] text-slate-500">
              {t('app.buyin')} <span className="font-bold text-yellow-400/80">{userBalance != null ? Math.min(200, userBalance) : 200}</span> {t('app.chips')}
            </div>
          </div>
          {startError && (
            <div className="rounded-xl bg-red-500/15 px-5 py-2.5 text-[16px] font-medium text-red-400 border border-red-500/20 shrink-0">
              {startError}
            </div>
          )}
          <button onClick={startGame} disabled={loading}
            className="relative rounded-xl px-10 py-3.5 text-xl font-bold text-white transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-blue-500/25 shrink-0"
            style={{ background: loading ? '#4b5563' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)' }}
          >
            {loading ? (<span className="flex items-center gap-2"><svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>{t('app.starting')}</span>) : t('app.start')}
          </button>
        </div>
        <div className="shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 border-t border-slate-700/50 bg-slate-900/95 backdrop-blur-md">
          <button onClick={() => setView('leaderboard')}
            className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
            style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.12), rgba(180,120,50,0.04))', borderColor: 'rgba(180,120,50,0.25)', color: 'rgba(251,191,36,0.9)' }}>
            <span className="text-lg">🏆</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.leaderboard_text')}</span>
          </button>
          <button onClick={() => setView('glory')}
            className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
            style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.12), rgba(180,120,50,0.04))', borderColor: 'rgba(180,120,50,0.25)', color: 'rgba(251,191,36,0.9)' }}>
            <span className="text-lg">👑</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.glory_text')}</span>
          </button>
          <button onClick={() => setShowMenu(true)}
            className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
            style={{ background: 'rgba(100,100,200,0.15)', borderColor: 'rgba(100,100,200,0.25)', color: '#a5b4fc' }}>
            <span className="text-lg">👤</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.me_text')}</span>
          </button>
        </div>
        {showMenu && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowMenu(false)}>
            <div className="w-full max-w-sm rounded-t-2xl bg-slate-900 border border-slate-700/50 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col py-2">
                <div className="flex items-center justify-center py-3 border-b border-slate-700/30"><div className="w-8 h-1 rounded-full bg-slate-600" /></div>
                <button onClick={() => { toggleLocale(); setShowMenu(false) }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                  <span className="text-lg">🌐</span><span className="flex-1 text-left">{t('app.lang_switch')}</span>
                  <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{locale === 'zh' ? '中文' : 'EN'}</span>
                </button>
                <button onClick={() => { setView('history'); setShowMenu(false) }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                  <span className="text-lg">📋</span><span className="flex-1 text-left">{t('app.history_title')}</span>
                </button>
                <button onClick={() => {
                  setShowMenu(false); const tg = window.Telegram?.WebApp; const url = 'https://poker.usdt.bz/s/play'; const text = t('app.share_topbar');
                  if (tg?.openTelegramLink) tg.openTelegramLink('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text));
                  else if (tg?.shareUrl) tg.shareUrl(url); else window.open('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text), '_blank')
                }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                  <span className="text-lg">🎁</span><span className="flex-1 text-left">{t('app.share_earn_chips')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {showShare && <ShareModal reason={shareReason} authToken={authToken} onClose={() => { setShowShare(false); setStartError(null) }} onShare={() => { setShowShare(false); onGameEnd?.(); startGame() }} />}
      </div>
    )
  }

  // ===== Game Table =====
  const isUserTurn = gameState.currentActor === 'user'
  const callAmount = gameState.currentBet - gameState.userBet
  const minRaise = gameState.currentBet * 2

  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      {showConfetti && <Confetti active={showConfetti} onComplete={() => setShowConfetti(false)} />}
      <div ref={chipLayerRef} className="fixed inset-0 z-50 pointer-events-none" />

      {/* Loading overlay when replaying */}
      {isStarting && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-900/90 px-8 py-6 border border-amber-500/20 shadow-2xl">
            <svg className="h-8 w-8 animate-spin text-amber-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm font-semibold text-amber-300/90">{t('app.starting')}</span>
            {startError && (
              <span className="text-xs text-red-400/80 mt-1">{startError}</span>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mx-4 rounded-xl bg-red-500/15 px-4 py-2.5 text-[16px] font-medium text-red-400 border border-red-500/20">
          {error}
        </div>
      )}

      {/* ===== Scrollable middle section ===== */}
      <div className="flex-1 overflow-y-auto px-2 pt-2 space-y-1">
        {/* AI Section */}
        <div ref={aiSectionRef} className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full overflow-hidden bg-white shadow-lg shadow-slate-900/50 ring-2 ring-amber-500/30">
              <img
                src={`/ai-avatars/${aiName.toLowerCase().replace(/\s+/g, '-')}.webp`}
                alt={aiName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const t = e.currentTarget
                  t.style.display = 'none'
                  const p = t.parentElement
                  if (p) {
                    const fb = p.querySelector('.ai-avatar-fallback') as HTMLElement
                    if (fb) fb.style.display = 'flex'
                  }
                }}
              />
              <span className="ai-avatar-fallback hidden text-lg">🤖</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-semibold text-slate-400 tracking-wide">{aiName}</span>
              <div className="flex items-center gap-1">
                <span className="text-yellow-400 text-[14px]">💰</span>
                <span className="text-[16px] font-bold text-white tabular-nums">
                  {gameState.aiChips.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-1.5 ai-cards">
            {(revealedAiHand || gameState.aiHand).map((card: any, i: number) => (
              <CardDisplay key={i} card={card} hidden={!isRevealing && !gameResult} />
            ))}
          </div>

          {gameState.lastAIAction && gameState.lastAIAction.action && gameState.currentActor === 'user' && (
            <div ref={aiLabelRef} className="rounded-full bg-amber-500/15 px-3 py-1 text-[13px] font-bold text-amber-400 border border-amber-500/20">
              {gameState.lastAIAction.action === 'call' && t('app.ai_action_call', { amount: String(gameState.lastAIAction.amount) })}
              {gameState.lastAIAction.action === 'raise' && t('app.ai_action_raise', { amount: String(gameState.lastAIAction.amount) })}
              {gameState.lastAIAction.action === 'bet' && t('app.ai_action_bet', { amount: String(gameState.lastAIAction.amount) })}
              {gameState.lastAIAction.action === 'check' && t('app.ai_action_check')}
              {gameState.lastAIAction.action === 'fold' && t('app.ai_action_fold')}
              {gameState.lastAIAction.action === 'allin' && t('app.ai_action_allin')}
            </div>
          )}

          {!isUserTurn && gameState.status === 'active' && (
            <div className="flex items-center gap-1.5 rounded-full bg-yellow-500/15 px-3 py-1 text-[13px] font-bold text-yellow-400 border border-yellow-500/20 animate-pulse">
              <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('app.thinking')}
            </div>
          )}
        </div>

        {/* Round bets */}
        <div className="flex w-full justify-between px-1">
          <div ref={aiBetRef} className="flex items-center gap-1 rounded-full bg-slate-700/80 px-2.5 py-0.5 border border-slate-500/50" style={{ transition: 'border-color 0.3s, box-shadow 0.3s' }}>
            <span className="text-[11px] text-amber-300 font-semibold">{aiName}</span>
            <span className="text-[13px] font-bold text-yellow-200 tabular-nums">{gameState.aiBet.toLocaleString()}</span>
          </div>
          <div ref={userBetRef} className="flex items-center gap-1 rounded-full bg-slate-700/80 px-2.5 py-0.5 border border-slate-500/50" style={{ transition: 'border-color 0.3s, box-shadow 0.3s' }}>
            <span className="text-[11px] text-blue-300 font-semibold">{t('app.you')}</span>
            <span className="text-[13px] font-bold text-yellow-200 tabular-nums">{gameState.userBet.toLocaleString()}</span>
          </div>
        </div>

        {/* Pot & Community Cards */}
        <div className="poker-table-oval mx-0 flex flex-col items-center justify-center gap-2 px-3 py-3">
          <div ref={potRef} className="animate-pulse-glow flex items-center gap-1.5 rounded-full bg-black/40 px-4 py-1.5">
            <span className="text-lg">💰</span>
            <span className="text-lg font-extrabold text-yellow-300 tabular-nums">
              {gameState.pot.toLocaleString()}
            </span>
          </div>

          <div className="flex gap-1 community-cards">
            {gameState.communityCards.map((card: any, i: number) => (
              <CardDisplay key={i} card={card} />
            ))}
            {Array.from({ length: 5 - gameState.communityCards.length }).map((_, i) => (
              <div key={`empty-${i}`} className="poker-card back" style={{ width: '56px', height: '80px', opacity: 0.5 }}>
                <span className="back-label-top">X8</span>
                <span className="back-label-bottom">Poker</span>
              </div>
            ))}
          </div>

          <div className="rounded-full bg-black/30 px-3 py-0.5">
            <span className="text-[12px] font-semibold text-emerald-300 uppercase tracking-widest">
              {t(ROUND_LABELS[gameState.currentRound] || gameState.currentRound)}
            </span>
          </div>

        </div>

        {/* Revealing overlay - fixed center */}
        {isRevealing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="animate-pulse rounded-full bg-white/90 px-6 py-3 text-base font-bold text-slate-800 border border-amber-500/30 shadow-xl">
              {t('app.revealing')}
            </div>
          </div>
        )}

        {/* User Section */}
        <div ref={userSectionRef} className="flex flex-col items-center gap-2">
          <div className="flex gap-1.5 user-cards">
            {gameState.userHand.map((card: any, i: number) => (
              <CardDisplay key={i} card={card} />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-full overflow-hidden bg-white shadow-lg shadow-blue-500/30 ring-2 ring-blue-400/30">
              {userPhotoUrl ? (
                <img src={userPhotoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl">👤</span>
              )}
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 flex items-center justify-center ring-1.5 ring-amber-200 shadow">
                <span className="text-[8px] font-extrabold text-white leading-none">D</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-semibold text-slate-400 tracking-wide">{t('app.you')}</span>
              <div className="flex items-center gap-1">
                <span className="text-yellow-400 text-[14px]">💰</span>
                <span className="text-[16px] font-bold text-white tabular-nums">
                  {gameState.userChips.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Bottom bar (fixed at bottom) ===== */}
      <div className="shrink-0">
        {/* Game Result Banner */}
        {gameState.status === 'completed' && gameResult && (
          <div className="mx-3 mb-2 rounded-2xl border bg-slate-900/95 px-5 py-4 backdrop-blur-md text-center"
            style={{
              borderColor: gameResult.winner === 'user' ? 'rgba(251, 191, 36, 0.4)' : 'rgba(239, 68, 68, 0.3)',
              boxShadow: gameResult.winner === 'user' ? '0 0 30px rgba(251, 191, 36, 0.15)' : '0 0 20px rgba(239, 68, 68, 0.1)',
            }}
          >
            <div className={`text-[22px] font-extrabold ${
              gameResult.winner === 'user'
                ? 'bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent'
                : 'text-red-400'
            }`}>
              {gameResult.winner === 'user' ? t(gameResult.winningHand ? 'app.win' : 'app.win_fold', { name: aiName }) : gameResult.winner === 'tie' ? t('app.tie') : t('app.lose')}
            </div>
            {handLabel && (
              <p className="text-[14px] text-slate-400 mt-0.5">{handLabel}</p>
            )}
            {(() => {
              const ic = gameState.initialChips || 0
              const uc = gameState.userChips || 0
              const pot = gameState.pot || 0
              const net = gameResult.winner === 'user'
                ? pot + uc - ic
                : gameResult.winner === 'tie'
                  ? Math.floor(pot / 2) + uc - ic
                  : uc - ic
              const prefix = net >= 0 ? '+' : ''
              return (
                <p className={`text-[16px] font-bold mt-1 ${net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {prefix}{net.toLocaleString()} {t('app.chips')}
                </p>
              )
            })()}
            {startError && (
              <div className="mx-2 mt-2 rounded-lg bg-red-500/15 px-3 py-2 text-[13px] font-medium text-red-400 border border-red-500/20">
                {startError}
              </div>
            )}
            <div className="flex gap-2 justify-center mt-3">
              <button
                onClick={handlePlayAgain}
                disabled={loading}
                className="rounded-xl px-6 py-2 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-50 shadow-lg flex-1"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
                }}
              >
                {loading ? t('app.starting') : t('app.replay')}
              </button>
              <button
                onClick={() => handleShareResult()}
                disabled={sharing}
                className="rounded-xl px-4 py-2 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
                }}
              >
                {sharing ? t('app.sharing') : t('app.share')}
              </button>
              <button
                onClick={() => setView('history')}
                className="rounded-xl px-4 py-2 text-sm font-bold transition-all active:scale-95 border"
                style={{
                  color: 'rgba(180,120,50,0.8)',
                  borderColor: 'rgba(180,120,50,0.25)',
                  background: 'rgba(180,120,50,0.06)',
                }}
              >
                {t('app.history')}
              </button>
            </div>
          </div>
        )}

        {/* Action Bar */}
        {!isRevealing && isGameActive && (
          <div className="pb-safe">
            <ActionBar
              isUserTurn={isUserTurn}
              currentBet={gameState.currentBet}
              userBet={gameState.userBet}
              userChips={gameState.userChips}
              callAmount={callAmount}
              minRaise={minRaise}
              onAction={playerAction}
              loading={loading}
            />
          </div>
        )}

        {/* Bottom Nav - shown on game result screen */}
        {gameResult && (
          <div className="flex items-center justify-center gap-2 px-4 py-2.5 border-t border-slate-700/50 bg-slate-900/95 backdrop-blur-md">
            <button onClick={() => setView('leaderboard')}
              className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
              style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.12), rgba(180,120,50,0.04))', borderColor: 'rgba(180,120,50,0.25)', color: 'rgba(251,191,36,0.9)' }}>
              <span className="text-lg">🏆</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.leaderboard_text')}</span>
            </button>
            <button onClick={() => setView('glory')}
              className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
              style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.12), rgba(180,120,50,0.04))', borderColor: 'rgba(180,120,50,0.25)', color: 'rgba(251,191,36,0.9)' }}>
              <span className="text-lg">👑</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.glory_text')}</span>
            </button>
            <button onClick={() => setShowMenu(true)}
              className="flex-1 min-w-0 rounded-xl px-2 py-2 transition-all active:scale-95 border flex flex-col items-center gap-0.5"
              style={{ background: 'rgba(100,100,200,0.15)', borderColor: 'rgba(100,100,200,0.25)', color: '#a5b4fc' }}>
              <span className="text-lg">👤</span><span className="text-[10px] font-bold leading-tight whitespace-nowrap">{t('app.me_text')}</span>
            </button>
          </div>
        )}
      </div>

      {showMenu && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowMenu(false)}>
          <div className="w-full max-w-sm rounded-t-2xl bg-slate-900 border border-slate-700/50 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col py-2">
              <div className="flex items-center justify-center py-3 border-b border-slate-700/30"><div className="w-8 h-1 rounded-full bg-slate-600" /></div>
              <button onClick={() => { toggleLocale(); setShowMenu(false) }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                <span className="text-lg">🌐</span><span className="flex-1 text-left">{t('app.lang_switch')}</span>
                <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{locale === 'zh' ? '中文' : 'EN'}</span>
              </button>
              <button onClick={() => { setView('history'); setShowMenu(false) }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                <span className="text-lg">📋</span><span className="flex-1 text-left">{t('app.history_title')}</span>
              </button>
              <button onClick={() => {
                setShowMenu(false); const tg = window.Telegram?.WebApp; const url = 'https://poker.usdt.bz/s/play'; const text = t('app.share_topbar');
                if (tg?.openTelegramLink) tg.openTelegramLink('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text));
                else if (tg?.shareUrl) tg.shareUrl(url); else window.open('https://t.me/share/url?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(text), '_blank')
              }} className="flex items-center gap-3 px-6 py-3.5 text-sm font-medium text-slate-300 hover:bg-slate-800/50">
                <span className="text-lg">🎁</span><span className="flex-1 text-left">{t('app.share_earn_chips')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showShare && (
        <ShareModal
          reason={shareReason}
          authToken={authToken}
          onClose={() => {
            setShowShare(false)
            setGameResult(null)
            resetState()
            setGameId(null)
            localStorage.removeItem('poker_game_id')
          }}
          onShare={() => {
            setShowShare(false)
            onGameEnd?.()
            startGame()
          }}
        />
      )}
    </div>
  )
}
